import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import useModal from "../../../shared/components/refactor/base/modal/useModal";
import MxModalTemplate2 from "@rf-l1/modal/MxModalTemplate2";
import { styled } from "@mui/material";
import MxTypography from "@rf-l1/MxTypography";
import { useLocation } from "react-router-dom";
import useUserHasPermission from "@/shared/hooks/use-useris.hook";
import { debounce } from "lodash";
import MxButton2 from "@rf-l1/buttons/MxButton2";

export interface WeeklyModalProps {
  modalKey: string;
}

const REAL_ESTATE_ALLOWED_PATH = "/realestates";

export function useWeeklyModal({ modalKey }: WeeklyModalProps) {
  const { modal, onClose, isOpenRef } = useModal();
  const { pathname } = useLocation();
  const { getPreferences, updatePreferences } = useUserHasPermission();
  const isAllowedPath = useMemo(() => {
    return pathname === REAL_ESTATE_ALLOWED_PATH;
  }, [pathname]);

  const markAsShown = useCallback(() => {
    const now = new Date();
    const nextWeek = new Date(now);
    nextWeek.setDate(now.getDate() + 7);

    const preferences = getPreferences();
    const weeklyModal = preferences.weeklyModal ?? {};

    updatePreferences({
      ...preferences,
      weeklyModal: {
        ...weeklyModal,
        [modalKey]: nextWeek.toISOString()
      }
    });
  }, [getPreferences, updatePreferences, modalKey]);

  const showModal = useCallback(() => {
    return modal({
      template: <WeeklyModalTemplate />,
      options: {
        disableCloseButton: true,
        disableEscapeKeyDown: true,
        disableBackdropClick: true,
        title: "",
        size: {
          xs: {
            width: "100%",
            height: "100%"
          },
          sm: {
            width: "100%",
            height: "100%"
          },
          md: {
            width: "100%"
          },
          lg: {
            width: "450px"
          },
          xl: {
            width: "450px"
          }
        }
      }
    }).then(e => {
      if (e?.type?.confirm) {
        markAsShown();
      }
    });
  }, [modal, markAsShown]);

  const shouldShowModal = useCallback(() => {
    const now = new Date();
    const today = now.getDay();
    const dayOfWeek = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6
    };

    if (![dayOfWeek.tuesday, dayOfWeek.thursday].includes(today)) {
      return false;
    }

    const preferences = getPreferences();
    const lastShown = preferences.weeklyModal?.[modalKey];
    if (!lastShown) return true;

    const lastShownDate = new Date(lastShown);
    return now > lastShownDate;
  }, [getPreferences, modalKey]);

  const checkAndShowModal = useCallback(() => {
    const showModalResult = shouldShowModal();
    if (!isAllowedPath || !showModalResult) {
      return;
    }

    showModal();
  }, [isAllowedPath, shouldShowModal, showModal]);

  const debouncedCheck = useMemo(
    () => debounce(checkAndShowModal, 1000, { leading: true, trailing: false }),
    [checkAndShowModal]
  );

  useEffect(() => {
    // Initial check
    const timeoutId = setTimeout(debouncedCheck, 1000);

    // Set up interval for weekly checks (every hour)
    const intervalId = setInterval(debouncedCheck, 60 * 60 * 1000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      debouncedCheck.cancel();
    };
  }, [debouncedCheck]);

  useEffect(() => {
    if (!isAllowedPath && isOpenRef.current) {
      onClose();
    }
  }, [isAllowedPath, isOpenRef, onClose]);

  return null;
}

export default useWeeklyModal;

const WeeklyModalTemplate = () => {
  const { onConfirm } = useModal();

  const goToMapMode = () => {
    document.getElementById("toggle-map-button")?.click();
    setTimeout(() => {
      onConfirm();
    }, 100);
  };

  return (
    <MxModalTemplate2>
      <Grid container spacing={2} p={2}>
        <Grid item xs={12} textAlign="center">
          <MxTypography variant="h6" fontWeight="bold" color="primary.main">
            Prueba el nuevo modo mapa
          </MxTypography>
        </Grid>

        <Grid item xs={12}>
          <MxTypography variant="caption" textAlign="center" color="text.secondary">
            Ahora puedes ver todas las propiedades sobre un mapa interactivo. Cambia
            al modo mapa y explora la zona que te interesa.
          </MxTypography>
        </Grid>

        <Grid item xs={12} mt={2}>
          <GifContainer>
            <img
              src="https://video-public.canva.com/VAFEx9l01Yo/v/b339cf2cd1.gif"
              alt="Weekly content"
              style={{ width: "100%", height: "auto", borderRadius: "8px" }}
            />
          </GifContainer>
        </Grid>

        <Grid item xs={12} mt={2} textAlign="center">
          <MxButton2 variant="contained" color="primary" onClick={goToMapMode}>
            Cambiar a vista de mapa
          </MxButton2>
        </Grid>
      </Grid>
    </MxModalTemplate2>
  );
};

const GifContainer = styled("div")({
  maxWidth: "100%",
  margin: "0 auto",
  borderRadius: "8px",
  overflow: "hidden",
  "& img": {
    display: "block",
    maxHeight: "200px",
    objectFit: "contain",
    margin: "0 auto"
  }
});
